import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Trophy } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import api from '../services/api';

function localDate() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export default function CreateOpenPlay() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [venues, setVenues] = useState([]);
  const [courts, setCourts] = useState([]);
  const [selectedCourtIds, setSelectedCourtIds] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [loadingCourts, setLoadingCourts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    venueId: '',
    name: '',
    description: '',
    date: localDate(),
    startTime: '08:00',
    endTime: '10:00',
    maxPlayers: '16',
    pricePerPlayer: '',
    skillLevel: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getVenues('approved');
        setVenues(Array.isArray(data) ? data.filter((venue) => venue.isActive !== false) : []);
        if (isEditing) {
          const openPlay = await adminService.getOpenPlay(id);
          setForm({
            venueId: openPlay.venueId,
            name: openPlay.name || '',
            description: openPlay.description || '',
            date: String(openPlay.date).slice(0, 10),
            startTime: String(openPlay.startTime || '').slice(0, 5),
            endTime: String(openPlay.endTime || '').slice(0, 5),
            maxPlayers: String(openPlay.maxPlayers ?? 16),
            pricePerPlayer: String(openPlay.pricePerPlayer ?? ''),
            skillLevel: openPlay.skillLevel || '',
          });
          setSelectedCourtIds((openPlay.courts || []).map((court) => court.courtId));
          await loadVenueCourts(openPlay.venueId);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to load ${isEditing ? 'Open Play details' : 'approved venues'}`);
      } finally {
        setLoadingVenues(false);
      }
    })();
  }, [id, isEditing]);

  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === form.venueId),
    [form.venueId, venues]
  );

  async function loadVenueCourts(venueId) {
    if (!venueId) return;
    setLoadingCourts(true);
    try {
      const response = await api.get(`/venues/${venueId}/courts`);
      const venueCourts = Array.isArray(response.data) ? response.data : [];
      setCourts(venueCourts.filter((court) => court.isActive !== false));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load venue courts');
    } finally {
      setLoadingCourts(false);
    }
  }

  async function selectVenue(venueId) {
    setForm((current) => ({ ...current, venueId }));
    setSelectedCourtIds([]);
    setCourts([]);
    await loadVenueCourts(venueId);
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function toggleCourt(courtId) {
    setSelectedCourtIds((current) => current.includes(courtId)
      ? current.filter((id) => id !== courtId)
      : [...current, courtId]);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!form.venueId || !form.name.trim() || form.pricePerPlayer === '') {
      toast.error('Complete all required fields');
      return;
    }
    if (form.endTime <= form.startTime) {
      toast.error('End time must be after start time');
      return;
    }
    if (Number(form.maxPlayers) < 2) {
      toast.error('Maximum players must be at least 2');
      return;
    }
    if (Number(form.pricePerPlayer) < 0) {
      toast.error('Price per player cannot be negative');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        venueId: form.venueId,
        name: form.name.trim(),
        description: form.description.trim(),
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        maxPlayers: Number(form.maxPlayers),
        pricePerPlayer: Number(form.pricePerPlayer),
        skillLevel: form.skillLevel || null,
        courtIds: selectedCourtIds.length ? selectedCourtIds : null,
      };
      const result = isEditing
        ? await adminService.updateOpenPlay(id, payload)
        : await adminService.createOpenPlay(payload);
      toast.success(isEditing ? 'Open Play updated successfully' : 'Open Play created successfully');
      navigate(isEditing ? `/openplays/${id}` : (result?.id ? `/openplays/${result.id}` : '/openplays'));
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      toast.error(apiMessage || (typeof error.response?.data === 'string' ? error.response.data : `Failed to ${isEditing ? 'update' : 'create'} Open Play`));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => navigate('/openplays')} className="admin-btn-outline !h-11 !rounded-xl !px-4">
          <ArrowLeft size={17} className="mr-2" /> Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">{isEditing ? 'Edit Open Play' : 'Set Up Open Play'}</h1>
          <p className="mt-1 text-sm text-[#667085]">{isEditing ? 'Update this session without changing its venue' : 'Create an Open Play session or tournament at an approved venue'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="admin-card">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E0F7F4]">
              <Trophy size={19} className="text-[#0B7F83]" />
            </div>
            <div>
              <h2 className="font-semibold text-[#1F2937]">Session details</h2>
              <p className="text-xs text-[#667085]">Required details players will see when browsing Open Play</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="venue" className="admin-label">Venue *</label>
              <select
                id="venue"
                value={form.venueId}
                onChange={(event) => selectVenue(event.target.value)}
                disabled={loadingVenues || isEditing}
                required
                className="admin-input"
              >
                <option value="">{loadingVenues ? 'Loading venues…' : 'Select an approved venue'}</option>
                {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name} — {venue.city}</option>)}
              </select>
              {!loadingVenues && venues.length === 0 && (
                <p className="mt-2 text-xs text-[#D46C4E]">No active approved venues are available.</p>
              )}
            </div>
            <div>
              <label htmlFor="name" className="admin-label">Session or tournament name *</label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                maxLength={120}
                required
                className="admin-input"
                placeholder="Saturday Social Mixer"
              />
            </div>
            <div>
              <label htmlFor="date" className="admin-label">Date *</label>
              <input id="date" type="date" min={localDate()} value={form.date} onChange={(event) => updateField('date', event.target.value)} required className="admin-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="startTime" className="admin-label">Start time *</label>
                <input id="startTime" type="time" value={form.startTime} onChange={(event) => updateField('startTime', event.target.value)} required className="admin-input" />
              </div>
              <div>
                <label htmlFor="endTime" className="admin-label">End time *</label>
                <input id="endTime" type="time" value={form.endTime} onChange={(event) => updateField('endTime', event.target.value)} required className="admin-input" />
              </div>
            </div>
            <div>
              <label htmlFor="maxPlayers" className="admin-label">Maximum players *</label>
              <input id="maxPlayers" type="number" min="2" step="1" value={form.maxPlayers} onChange={(event) => updateField('maxPlayers', event.target.value)} required className="admin-input" />
            </div>
            <div>
              <label htmlFor="price" className="admin-label">Price per player (PHP) *</label>
              <input id="price" type="number" min="0" step="0.01" value={form.pricePerPlayer} onChange={(event) => updateField('pricePerPlayer', event.target.value)} required className="admin-input" placeholder="0 for a free session" />
            </div>
            <div>
              <label htmlFor="skillLevel" className="admin-label">Skill level</label>
              <select id="skillLevel" value={form.skillLevel} onChange={(event) => updateField('skillLevel', event.target.value)} className="admin-input">
                <option value="">All skill levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Professional">Professional</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="description" className="admin-label">Description</label>
              <textarea
                id="description"
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
                rows="4"
                maxLength={1000}
                className="admin-input !h-auto"
                placeholder="Share the format, inclusions, rules, or anything players should bring."
              />
            </div>
          </div>
        </section>

        <section className="admin-card">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-[#1F2937]">Reserve courts</h2>
              <p className="text-xs text-[#667085]">Select the courts used by this session so scheduling conflicts can be prevented.</p>
            </div>
            {courts.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedCourtIds(selectedCourtIds.length === courts.length ? [] : courts.map((court) => court.id))}
                className="text-sm font-semibold text-[#0B7F83] hover:underline"
              >
                {selectedCourtIds.length === courts.length ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>

          <div className="mt-5">
            {!form.venueId ? (
              <p className="rounded-xl bg-[#F8FAFC] p-4 text-sm text-[#667085]">Choose a venue to see its courts.</p>
            ) : loadingCourts ? (
              <div className="flex items-center gap-3 py-4 text-sm text-[#667085]">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#264D59] border-t-transparent" /> Loading courts…
              </div>
            ) : courts.length === 0 ? (
              <p className="rounded-xl bg-[#F8FAFC] p-4 text-sm text-[#667085]">{selectedVenue?.name} has no active courts to select.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {courts.map((court) => {
                  const selected = selectedCourtIds.includes(court.id);
                  return (
                    <button
                      type="button"
                      key={court.id}
                      aria-pressed={selected}
                      onClick={() => toggleCourt(court.id)}
                      className={`flex items-center justify-between rounded-[16px] border p-4 text-left transition-colors ${selected ? 'border-[#264D59] bg-[#E0F7F4]' : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]'}`}
                    >
                      <span>
                        <span className="block text-sm font-semibold text-[#1F2937]">{court.name}</span>
                        <span className="mt-0.5 block text-xs text-[#667085]">{court.courtType || 'Court'}</span>
                      </span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${selected ? 'border-[#264D59] bg-[#264D59] text-white' : 'border-[#CBD5E1] text-transparent'}`}>
                        <Check size={14} />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={() => navigate('/openplays')} disabled={submitting} className="admin-btn-outline">Cancel</button>
          <button type="submit" disabled={submitting || loadingVenues || venues.length === 0} className="admin-btn-primary">
            {submitting ? (isEditing ? 'Saving changes…' : 'Creating Open Play…') : (isEditing ? 'Save changes' : 'Create Open Play')}
          </button>
        </div>
      </form>
    </div>
  );
}
